<template>
    <div>
        <div v-if="cart">
            <section class="detail cf pdg" data-pdgb="80" v-if="cart.cartQuantity !== 0">
                <div class="cart">
                    <table class="table">
                        <thead class="header">
                            <tr class="row">
                                <td class="product">{{ localizer.get("Checkout.Common.ProductDescription") }}</td>
                                <td class="qty">{{ localizer.get("Checkout.Common.Quantity") }}</td>
                                <td class="price">{{ localizer.get("Checkout.Common.Price") }}</td>
                                <td class="total">{{ localizer.get("Checkout.Common.Total") }}</td>
                            </tr>
                        </thead>
                        <tbody class="rows" v-for="cartRow in cart.cartRows">
                            <tr class="row">
                                <td class="product cf">
                                    <div class="image" v-if="cartRow.productImageUrl">
                                        <a :href="cartRow.productUrl">
                                            <img :src="cartRow.productImageUrl" width="100" height="100" alt="" loading="lazy" />
                                        </a>
                                    </div>
                                    <div class="desc">
                                        <a :href="cartRow.productUrl" class="title">{{ cartRow.productTitle }}</a>
                                        <div class="variant"><span class="label">{{ cartRow.variantTitle }}</span>{{ cartRow.variantDescription }}</div>
                                        <div class="cod">cod. {{ cartRow.productCode }}</div>
                                    </div>
                                </td>
                                <td class="qty">{{ cartRow.quantity }}</td>
                                <td class="price"><span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span>{{ cartRow.unitPrice }}</td>
                                <td class="total"><span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span>{{ cartRow.rowPrice }}</td>
                            </tr>
                        </tbody>
                    </table>
                    <div class="calltoaction mrg" data-mrgt="20" data-color="greyl"><a :href="routes.Cart_Detail" class="button">{{ localizer.get("Checkout.Common.EditCart") }}<span class="icon zmdi zmdi-edit"></span></a></div>
                    <div class="coupon" v-bind:class="{ active: cart.isCoupon }">
                        <div class="input">
                            <div class="control">
                                <input v-model.lazy="couponCode" id="CouponCode" name="CouponCode" type="text" @keydown.enter.prevent.stop="setCouponCode()" :placeholder="localizer.get('Checkout.Common.InsertCoupon')" class="input textbox disable-on-submit" maxlength="50" />
                            </div>
                            <div class="calltoaction" data-color="greyl">
                                <div v-if="!isLoadingForm" class="button disable-on-submit" @click="setCouponCode()">{{ localizer.get("Checkout.Common.Apply") }}</div>
                                <div v-else class="button disable-on-submit">{{ localizer.get("Checkout.Common.Apply") }}</div>
                            </div>
                            <div class="note"><em>{{ localizer.get("Checkout.Common.CouponDisclaimer") }}</em></div>
                        </div>
                        <div class="discount" v-if="cart.isCoupon">
                            <div class="description"><em>{{ cart.couponDescription }}</em></div>
                            <div class="total" v-if="cart.couponDiscount !== 0">
                                <span v-if="cart.couponDiscountMode === 'Amount'">
                                    <span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span>-{{ formatPrice(cart.couponDiscount) }}
                                </span>
                                <span v-if="cart.couponDiscountMode === 'Percentage'">
                                    -{{ cart.couponDiscount }}<span class="unit">%</span>
                                </span>
                            </div>
                            <div class="alert" v-if="cart.couponMinimumCartTotal && cart.couponMinimumGap !== 0">{{ localizer.get("Checkout.Common.CouponGap_Start") }} {{ formatPrice(cart.couponMinimumGap) }} {{ localizer.get("Checkout.Common.CouponGap_End") }}</div>
                        </div>
                    </div>
                    <div class="footer">
                        <div class="data">
                            <template v-if="cart.shippingMode != 'ExWork'">
                                <div class="shipping">
                                    <div class="shipping-cost" v-if="!cart.isFreeShippingCosts">{{ localizer.get("Checkout.Common.Shipping") }}: {{ localizer.get("Catalogue.Common.Euro") }} {{ cart.shippingCosts }}</div>
                                    <div class="shipping-cost" data-free="true" v-else>{{ localizer.get("Checkout.Common.FreeShipping") }}</div>
                                </div>
                            </template>
                            <template v-else-if="cart.shippingMode == 'ExWork'">
                                <div class="shipping" data-type="note">
                                    {{ localizer.get("Checkout.Common.Shipping_ExWork_Note") }} <a :href="routes.Support_ShippingReturns + '#ExWorks'" class="link">{{ localizer.get("App.Common.ShippingExWork_More") }}</a>
                                </div>
                            </template>
                            <template v-else-if="cart.shippingMode == 'WarehousePickup'">
                                <div class="shipping" data-type="note">
                                    {{ localizer.get("Checkout.Common.Shipping_WarehousePickup_Note") }}
                                </div>
                            </template>
                            <div class="total">
                                <template v-if="cart.shippingMode != 'ExWork'">
                                    {{ localizer.get("Checkout.Common.TotalIvaShipping") }}
                                </template>
                                <template v-else>
                                    {{ localizer.get("Checkout.Common.TotalIva") }}
                                </template>
                                <span class="unit">{{ localizer.get("Catalogue.Common.Euro") }}</span>
                                <span id="CartTotal" class="value">{{ cart.cartTotalWithCosts }}</span>
                            </div>
                            <div class="minimumtotal" v-if="cart.minimumTotalGap">{{ localizer.get("Checkout.Common.MinimumTotal") }} {{ cart.minimumOrderTotal }}</div>
                            <div class="minimumtotalgap" v-if="cart.minimumTotalGap">{{ localizer.get("Checkout.Common.MinimumTotalGap_Start") }} {{ cart.minimumTotalGap }} {{ localizer.get("Checkout.Common.MinimumTotalGap_End") }}</div>
                            <div class="payment-method form">
                                <div class="control">
                                    <label class="label">{{ localizer.get("Checkout.Common.PaymentMethod") }}</label>
                                    <select class="dropdown" v-model.lazy="paymentMethod" @change="setPaymentMethod()">
                                        <option value="PayPal">{{ localizer.get("Checkout.Common.PayPal") }}</option>
                                        <option value="BankTransfer">{{ localizer.get("Checkout.Common.BankTransfer") }}</option>
                                    </select>
                                </div>
                            </div>
                            <div class="disclaimer mrg" data-mrgt="10">
                                <span v-html="localizer.get('Checkout.Common.ConfirmTermsOfSale', routes.Terms_TermsOfSale, localizer.get('Checkout.Common.ConfirmTermsOfSale_Button'))"></span>
                            </div>
                            <div class="calltoaction" data-size="big">
                                <button v-if="!isLoadingForm" class="button disable-on-submit" @click="placeOrder()">{{ localizer.get("Checkout.Common.BuyNow") }}</button>
                                <span v-else class="button disable-on-submit">{{ localizer.get("Checkout.Common.BuyNow") }}</span>
                            </div>
                            <div class="valid-summary">
                                <div v-for="(validationError, index) in validationErrors">{{ validationError.message }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section v-else class="detail cf pdg" data-pdgb="80">
                <div class="title">{{ localizer.get("Checkout.Common.CheckoutError_Title") }}</div>
                <div class="subtitle mrg" data-mrgt="20" data-line="false">{{ localizer.get("Checkout.Common.CheckoutError_Abstract") }}</div>
                <div class="mrg" data-mrgt="40">
                    <span v-html="localizer.get('Checkout.Common.CheckoutError_Description', routes.Support_Contact, localizer.get('Checkout.Common.CheckoutError_Description_Link'))"></span>
                </div>
            </section>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onUpdated, nextTick } from "vue";
import { useStore } from "vuex";
import Axios from "axios";
import GTagHelper from "@Scripts/Infrastructure/GTagHelper";
import Localizer from "@Scripts/Common/Localizer";
import FormHelper from "@Scripts/Common/FormHelper";

const store = useStore();

const localizer = Localizer;
const routes = (window as any).App.routes;

const validationErrors = ref<any[]>([]);
const isLoadingForm = ref(false);
const isTermsApproved = ref(false);
const isGAEventSent = ref(false);
const couponCode = ref<string>(null);
const paymentMethod = ref<string>("PayPal");

const cart = computed(() => {
    let cartData = store.getters["cartStore/cart"];

    couponCode.value = cartData ? cartData.couponCode : null;

    paymentMethod.value = cartData ? cartData.paymentMethod : "PayPal";
    return cartData;
});

async function setCouponCode() {
    FormHelper.removeFocus();

    isLoadingForm.value = true;

    validationErrors.value = [];

    try {
        let response = (await Axios.post(routes.Cart_SetCouponCode, { couponCode: couponCode.value })).data;

        store.commit("cartStore/setCart", response.cartInfo);
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }

    isLoadingForm.value = false;
}

async function setPaymentMethod() {
    FormHelper.removeFocus();

    isLoadingForm.value = true;

    validationErrors.value = [];

    try {
        let response = (await Axios.post(routes.Cart_SetPaymentMethod, { paymentMethod: paymentMethod.value })).data;

        store.commit("cartStore/setCart", response.cartInfo);
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }

    isLoadingForm.value = false;
}

function formatPrice(data: string) {
    return Number(data).toFixed(2).replace(".", ",");
}

async function placeOrder() {
    FormHelper.removeFocus();

    isLoadingForm.value = true;

    validationErrors.value = [];

    try {
        const response = (await Axios.post(routes.Checkout_Summary)).data;

        window.location = response.redirectUrl;
    } catch (error: any) {
        if (error.response && error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }

    isLoadingForm.value = false;
}

onUpdated(() => {
    nextTick(() => {
        if (cart.value && !isGAEventSent.value) {
            var itemList = [];

            for (var cartRow in cart.value.cartRows) {
                itemList.push({
                    item_id: cart.value.cartRows[cartRow].productCode,
                    item_name: cart.value.cartRows[cartRow].productTitle,
                    affiliation: "Technical Ceramic",
                    currency: "EUR",
                    item_category: cart.value.cartRows[cartRow].categoryName,
                    price: parseFloat(cart.value.cartRows[cartRow].unitPrice.replace(',', '.')),
                    quantity: cart.value.cartRows[cartRow].quantity
                });
            }

            GTagHelper.sendCustomEvent("begin_checkout", {
                currency: "EUR",
                value: parseFloat(cart.value.cartTotalWithCosts.replace(',', '.')),
                coupon: cart.value.CouponCode,
                items: itemList
            });

            isGAEventSent.value = true;
        }
    });
});
</script>